import { useEffect, useState, type FocusEvent } from 'react';
import { api, type TicketAuswahl, type TicketAuswahllisten, type TicketEinstellungen } from '../api';
import { Icon } from './Icon';
import { toast } from './toast';
import { Check, Choice, Field, type Option } from './fields';
import * as A from '../logik/actions.js';
import { verantwortliche } from '../logik/model.js';

interface SettingsProps {
  data: any;
  istAdmin: boolean;
  /** Reiter „Archivieren“ (nur Admins des Projekts); ohne Angabe kein Reiter. */
  archiv?: { archiviert: boolean; archivieren: () => void; wiederherstellen: () => void };
  onClose: () => void;
}

export function Settings({ data, istAdmin, archiv, onClose }: SettingsProps) {
  const [tab, setTab] = useState('allgemein');
  const m = data.meta;
  const ro = !istAdmin;
  const usedLanes = new Set<string>(data.nodes.map((n: any) => n.lane));
  const usedSystems = new Set<string>(data.nodes.flatMap((n: any) => n.systemIds));
  const usedTypes = new Set<string>(data.edges.map((e: any) => e.typ));

  const tabs = [['allgemein', 'Allgemein'], ['lanes', 'Bahnen'], ['systems', 'Systeme'], ['edgeTypes', 'Verbindungsarten'], ['verantwortliche', 'Verantwortliche'],
    ['tickets', 'Ticketsystem'], ...(archiv ? [['archiv', 'Archivieren']] : [])];
  return (
    <div className="overlay" role="presentation">
      <div className="dialog" role="dialog" aria-modal="true" aria-label="Einstellungen" data-testid="settings">
        <button className="btn icon close" onClick={onClose}><Icon name="x" /></button>
        <h2>Einstellungen</h2>
        {ro && <p className="hint" data-testid="settings-readonly">{archiv?.archiviert
          ? 'Archiviertes Projekt – nur lesbar. Wiederherstellen im Reiter „Archivieren“.'
          : 'Nur Admins können die Einstellungen ändern.'}</p>}
        <div className="tabs">{tabs.map(([k, l]) => <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)} data-testid={'settings-tab-' + k}>{l}</button>)}</div>

        {tab === 'allgemein' && (
          <div>
            <div className="grid2">
              <Field label="Kunde" value={m.kunde} onCommit={() => {}} disabled hint={KUNDE_HINWEIS} />
              <Field label="Erstellt von" value={m.erstelltVon} onCommit={(v) => A.setMeta('erstelltVon', v)} disabled={ro} />
            </div>
            <Field label="Titel" value={m.titel} onCommit={(v) => A.setMeta('titel', v)} disabled={ro} />
            <Field label="Untertitel" value={m.untertitel} onCommit={(v) => A.setMeta('untertitel', v)} disabled={ro} />
            <div className="grid2">
              <Field label="Version" value={m.version} onCommit={(v) => A.setMeta('version', v)} disabled={ro} testid="meta-version" hint="Zählt bei Änderungen selbst hoch; eine hier eingetragene Version (z. B. 1.0) gilt ab sofort." />
              <Field label="Stand" type="date" value={m.stand} onCommit={(v) => A.setMeta('stand', v)} disabled={ro} />
            </div>
            <Field label="Einleitung auf der Prozesslandkarte (Markdown)" value={m.beschreibung} onCommit={(v) => A.setMeta('beschreibung', v)} multiline tall disabled={ro} />
            <div className="field"><span>Kundenlogo (erscheint neben dem IWM-Logo, in Exporten und im Druck)</span>
              <Kundenlogo data={data} />
            </div>
            <p className="hint">Projekt-ID: <code>{m.id}</code></p>
          </div>
        )}

        {tab === 'lanes' && (
          <CollTable coll="lanes" items={data.lanes} used={usedLanes} ro={ro}
            cols={[['name', 'Name', 'text'], ['beschreibung', 'Beschreibung', 'text'], ['farbe', 'Farbe', 'color']]}
            onAdd={() => A.addCollectionItem('lanes', { name: 'Neue Bahn', farbe: '#6E7C85', beschreibung: '' })}
            hint="Bahnen sind die Firmen oder Rollen (Swimlanes). Bahnen mit Schritten können nicht gelöscht werden." />
        )}
        {tab === 'systems' && (
          <CollTable coll="systems" items={data.systems} used={usedSystems} ro={ro}
            cols={[['name', 'Name', 'text'], ['kurz', 'Kürzel', 'text'], ['beschreibung', 'Beschreibung (Infotext)', 'text'], ['farbe', 'Farbe', 'color']]}
            onAdd={() => A.addCollectionItem('systems', { name: 'Neues System', kurz: 'NEU', farbe: '#009CDE' })}
            hint="Systeme erscheinen als Kürzel an den Schritten. Systeme, die Schritten zugeordnet sind, können nicht gelöscht werden." />
        )}
        {tab === 'verantwortliche' && <Verantwortliche data={data} ro={ro} />}
        {tab === 'tickets' && <TicketEinstellungenReiter projektId={m.id} ro={ro} />}
        {tab === 'archiv' && archiv && (
          <div data-testid="settings-archiv">
            {archiv.archiviert ? (
              <>
                <p>Das Projekt ist archiviert und nur lesbar. Nach Ablauf der Aufbewahrungsfrist wird es endgültig gelöscht.</p>
                <button className="btn" onClick={archiv.wiederherstellen} data-testid="settings-wiederherstellen"><Icon name="undo" /> Wiederherstellen</button>
              </>
            ) : (
              <>
                <p>Ein archiviertes Projekt ist nur noch lesbar, wird auf der Startseite ausgeblendet und nach Ablauf der
                  Aufbewahrungsfrist endgültig gelöscht. Bis dahin lässt es sich wiederherstellen.</p>
                <button className="btn" onClick={archiv.archivieren} data-testid="projekt-archivieren"><Icon name="folder" /> Projekt archivieren …</button>
              </>
            )}
          </div>
        )}
        {tab === 'edgeTypes' && (
          <CollTable coll="edgeTypes" items={data.edgeTypes} used={usedTypes} ro={ro}
            cols={[['name', 'Name', 'text'], ['kennung', 'Kennung', 'text'], ['dash', 'Strichmuster', 'text'], ['farbe', 'Farbe', 'color']]}
            onAdd={() => A.addCollectionItem('edgeTypes', { name: 'Neue Verbindungsart', farbe: '#748694', dash: '', breite: 1.8 })}
            hint="Strichmuster: leer = durchgezogen, z. B. „6 3“ = gestrichelt. Die Kennung erscheint an jeder Verbindung dieser Art (z. B. EDI)." />
        )}
      </div>
    </div>
  );
}

/**
 * Reiter „Ticketsystem“: Tickets für das Projekt einschalten, checkITeasy-Projekt, Queue und Kategorie wählen.
 * Anders als die übrigen Reiter gehört das nicht zu den Prozessdaten – jede Änderung wird sofort gespeichert.
 */
function TicketEinstellungenReiter({ projektId, ro }: { projektId: string; ro: boolean }) {
  const [e, setE] = useState<TicketEinstellungen | null>(null);
  const [listen, setListen] = useState<TicketAuswahllisten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => {
    let aktuell = true;
    api.projektTicketsystem(projektId).then((x) => {
      if (!aktuell) return;
      setE(x);
      if (x.eingerichtet && x.kto)
        api.projektTicketsystemAuswahl(projektId).then((l) => aktuell && setListen(l)).catch((err) => aktuell && setFehler((err as Error).message));
    }).catch((err) => aktuell && setFehler((err as Error).message));
    return () => { aktuell = false; };
  }, [projektId]);
  const setzen = async (aenderung: Parameters<typeof api.projektTicketsystemSetzen>[1]) => {
    if (laeuft) return;
    setLaeuft(true);
    try { setE(await api.projektTicketsystemSetzen(projektId, aenderung)); }
    catch (err) { toast((err as Error).message, true); }
    finally { setLaeuft(false); }
  };
  if (!e) return <p className="hint" data-testid="settings-tickets">{fehler ?? 'Wird geladen …'}</p>;
  const moeglich = e.eingerichtet && !!e.kto;
  // Gespeicherte Werte bleiben wählbar, auch wenn die Liste (noch) nicht da ist oder den Wert nicht mehr enthält
  const optionen = (liste: TicketAuswahl[] | undefined, id: number | null, name: string | null, leer?: string): Option[] => [
    ...(leer ? [{ value: '', label: leer }] : []),
    ...(liste ?? []).map((x) => ({ value: String(x.id), label: x.name })),
    ...(id !== null && !(liste ?? []).some((x) => x.id === id) ? [{ value: String(id), label: name ?? '#' + id }] : []),
  ];
  const zahl = (v: string) => (v ? Number(v) : null);
  const gesperrt = ro || laeuft || !moeglich;
  return (
    <div data-testid="settings-tickets">
      <p className="hint">Aus Aufgaben dieses Projekts lassen sich Tickets im IWM-Ticketsystem checkITeasy anlegen und abgleichen. Änderungen in diesem Reiter gelten sofort.</p>
      {!e.eingerichtet && <p className="warnung" data-testid="tickets-nicht-eingerichtet">Das Ticketsystem ist noch nicht eingerichtet. Das Token hinterlegt ein Superadmin auf der Startseite unter „Ticketsystem …“.</p>}
      <div className="field"><span>Kunde in checkITeasy (nur zur Anzeige)</span>
        <p style={{ margin: 0 }} data-testid="tickets-kunde">{e.kto
          ? <>{e.kundeName ?? e.kto} <span className="hint">({e.kto})</span></>
          : <span className="warnung">Noch kein Kunde zugeordnet – das macht ein Superadmin auf der Startseite beim Kunden unter „Bearbeiten“.</span>}</p>
      </div>
      <div data-testid="tickets-aktiv">
        <Check label="Tickets für dieses Projekt" checked={e.aktiv} disabled={ro || laeuft || (!moeglich && !e.aktiv)} onChange={(v) => void setzen({ aktiv: v })} />
      </div>
      <div className="field"><span>checkITeasy-Projekt (optional – jedes Ticket wird diesem Projekt zugewiesen)</span>
        <Choice value={e.ciProjektId === null ? '' : String(e.ciProjektId)} disabled={gesperrt} testid="tickets-projekt"
          options={optionen(listen?.projekte, e.ciProjektId, e.ciProjektName, '– kein Projekt –')} onChange={(v: string) => void setzen({ ciProjektId: zahl(v) })} />
      </div>
      <div className="grid2">
        <div className="field"><span>Queue</span>
          <Choice value={e.queueId === null ? '' : String(e.queueId)} disabled={gesperrt} testid="tickets-queue"
            options={optionen(listen?.queues, e.queueId, e.queueName)} onChange={(v: string) => v && void setzen({ queueId: zahl(v) })} />
        </div>
        <div className="field"><span>Kategorie</span>
          <Choice value={e.kategorieId === null ? '' : String(e.kategorieId)} disabled={gesperrt} testid="tickets-kategorie"
            options={optionen(listen?.kategorien, e.kategorieId, e.kategorieName)} onChange={(v: string) => v && void setzen({ kategorieId: zahl(v) })} />
        </div>
      </div>
      <p className="hint">Beim Einschalten werden Queue und Kategorie mit „Software“ und „Projekt“ vorbelegt, sofern es sie in checkITeasy gibt.</p>
      {fehler && <p className="hint warnung" data-testid="tickets-fehler">Auswahllisten nicht abrufbar: {fehler}</p>}
      {e.aktiv && <p className="hint" data-testid="tickets-abgleich">Letzter Abgleich: {e.letzterSync ? new Date(e.letzterSync).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : 'noch keiner'}{e.syncFehler ? ` · Fehler: ${e.syncFehler}` : ''}</p>}
    </div>
  );
}

/** Kunde und Kundenlogo gehören zum Kunden: Der Server liest sie von dort, im Projekt sind sie nur zur Anzeige. */
export const KUNDE_HINWEIS = 'Name und Logo des Kunden ändern Superadmins auf der Startseite (beim Kunden „Bearbeiten“).';

/** Kundenlogo nur anzeigen; geändert wird es beim Kunden (Startseite › Kunde › Bearbeiten, siehe KUNDE_HINWEIS am Feld „Kunde“). */
export function Kundenlogo({ data }: { data: any }) {
  const m = data.meta;
  return (
    <div data-testid="kundenlogo">
      {m.logoKunde ? <img className="logo-prev" src={m.logoKunde} alt="Kundenlogo" /> : <span className="hint">Kein Kundenlogo – es wird nur das IWM-Logo verwendet.</span>}
    </div>
  );
}

/** Liste aller Verantwortlichen; Umbenennen wirkt an allen Schritten und Entscheidungen. */
export function Verantwortliche({ data, ro }: { data: any; ro?: boolean }) {
  const list = verantwortliche(data);
  const rename = (from: string) => (e: FocusEvent<HTMLInputElement>) => {
    const to = e.target.value.trim();
    if (to && to !== from) { A.renameVerantwortlich(from, to); toast(`„${from}“ → „${to}“ umbenannt.`); }
    else e.target.value = from;
  };
  return (
    <div>
      {list.length === 0 && <p className="hint">Noch keine Verantwortlichen eingetragen.</p>}
      {list.length > 0 && (
        <table>
          <thead><tr><th>Name</th><th style={{ width: '110px' }}>Verwendet</th></tr></thead>
          <tbody>{list.map((v: any) => (
            <tr key={v.name}>
              <td><input className="inp" defaultValue={v.name} disabled={ro} onBlur={rename(v.name)} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} /></td>
              <td>{v.anzahl}×</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      <p className="hint">Die Liste entsteht aus allen Schritten und Entscheidungen. Wer einmal eingetragen ist, wird an jedem Feld „Verantwortlich“ vorgeschlagen. Umbenennen ändert den Namen überall auf einmal.</p>
    </div>
  );
}

interface CollTableProps {
  coll: string;
  items: any[];
  used: Set<string>;
  cols: [string, string, 'text' | 'color'][];
  onAdd: () => void;
  hint: string;
  ro?: boolean;
}

export function CollTable({ coll, items, used, cols, onAdd, hint, ro }: CollTableProps) {
  return (
    <div>
      <table>
        <thead><tr>{cols.map(([f, l]) => <th key={f}>{l}</th>)}<th></th></tr></thead>
        <tbody>{items.map((it) => (
          <tr key={it.id}>
            {cols.map(([f, , t]) => (
              <td key={f} style={t === 'color' ? { width: '50px' } : undefined}>{t === 'color'
                ? <input type="color" value={it[f]} disabled={ro} onChange={(e) => A.setCollectionItem(coll, it.id, f, e.target.value.toUpperCase())} />
                : <input className="inp" value={it[f] || ''} disabled={ro} onChange={(e) => A.setCollectionItem(coll, it.id, f, e.target.value)} />}</td>
            ))}
            <td style={{ width: '40px' }}>{!ro && !used.has(it.id) && !(coll === 'edgeTypes' && items.length < 2) && <button className="btn icon small" title="Löschen" onClick={() => A.removeCollectionItem(coll, it.id)}><Icon name="trash" /></button>}</td>
          </tr>
        ))}</tbody>
      </table>
      {!ro && <div className="actions" style={{ marginTop: '10px' }}><button className="btn small" onClick={onAdd}><Icon name="plus" /> Hinzufügen</button></div>}
      <p className="hint">{hint}</p>
    </div>
  );
}
